/**
 * CanvasConfig - Configuration for CanvasApp_v2
 * Defines spatial node types and canvas-specific settings
 */

import { AppConfig, SupportedClass } from '../../../services/baseApplication/types';

/**
 * Frame size presets — width x height in pixels.
 * Used by the toolbar dropdown and the AI (canvas_write MANIFEST).
 */
export const FRAME_PRESETS: Record<string, { label: string; width: number; height: number }> = {
  '1080p':          { label: '1080p (1920×1080)',          width: 1920, height: 1080 },
  'instagram-post': { label: 'Instagram Post (1080×1080)', width: 1080, height: 1080 },
  'instagram-story':{ label: 'Instagram Story (1080×1920)',width: 1080, height: 1920 },
  'letter':         { label: 'Letter (816×1056)',           width: 816,  height: 1056 },
  'a4':             { label: 'A4 (794×1123)',               width: 794,  height: 1123 },
  'meme-square':    { label: 'Meme Square (800×800)',       width: 800,  height: 800  },
  'meme-classic':   { label: 'Meme Classic (800×600)',      width: 800,  height: 600  },
  'twitter-post':   { label: 'Twitter/X Post (1200×675)',   width: 1200, height: 675  },
};

/**
 * Canvas-specific supported classes
 * Uses center-based coordinate system: (0, 0) is at center of viewport
 * Negative coords = top-left quadrants, Positive coords = bottom-right quadrants
 */
export const CANVAS_SUPPORTED_CLASSES: Record<string, SupportedClass> = {
  // Frames
  'canvas-frame': {
    displayName: 'Frame',
    description: 'Named artboard/viewport boundary for organizing and exporting content',
    category: 'frame',
    defaultAttrs: {
      position: { type: 'absolute', x: -960, y: -540, width: 1920, height: 1080 },
      style: { fill: '#ffffff', stroke: '#94a3b8', strokeWidth: 1 }
    }
  },

  // Containers
  'canvas-zone': {
    displayName: 'Canvas Zone',
    description: 'Spatial container for canvas elements',
    category: 'container',
    defaultAttrs: {
      position: { type: 'absolute', x: 0, y: 0, width: 1920, height: 1080 },
      style: { background: '#ffffff' }
    }
  },

  // Shapes
  'shape-rectangle': {
    displayName: 'Rectangle',
    description: 'Rectangular shape',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -100, y: -75, width: 200, height: 150 },
      style: { fill: '#4A90E2' }
    }
  },

  'shape-circle': {
    displayName: 'Circle',
    description: 'Circular shape',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -75, y: -75, width: 150, height: 150 },
      style: { fill: '#50C878' }
    }
  },

  'shape-ellipse': {
    displayName: 'Ellipse',
    description: 'Elliptical shape',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -100, y: -60, width: 200, height: 120 },
      style: { fill: '#FF6B6B' }
    }
  },

  'shape-triangle': {
    displayName: 'Triangle',
    description: 'Triangular shape',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -75, y: -75, width: 150, height: 150 },
      style: { fill: '#FFD93D' }
    }
  },

  'shape-line': {
    displayName: 'Line',
    description: 'Straight line with independently manipulatable endpoints',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -110, y: -60, width: 220, height: 120 },
      style: { stroke: '#333333', strokeWidth: 2 },
      semantic: { x1: 10, y1: 10, x2: 210, y2: 110 } // Endpoints relative to position
    }
  },

  'shape-arrow': {
    displayName: 'Arrow',
    description: 'Arrow connector with independently manipulatable endpoints',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -110, y: -60, width: 220, height: 120 },
      style: { stroke: '#333333', strokeWidth: 2, arrowEnd: true },
      semantic: { x1: 10, y1: 10, x2: 210, y2: 110 } // Endpoints relative to position
    }
  },

  'shape-custom': {
    displayName: 'Custom Shape',
    description: 'Custom vector shape with SVG path',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: -100, y: -100, width: 200, height: 200 },
      style: { fill: '#4A90E2' },
      semantic: { path: '' }
    }
  },

  // Text Elements
  'canvas-text': {
    displayName: 'Text Box',
    description: 'Freeform text element',
    category: 'text',
    defaultAttrs: {
      position: { type: 'absolute', x: -100, y: -25, width: 200, height: 50 },
      style: { fontSize: 16, color: '#000000', fontFamily: 'Arial', textAlign: 'center' }
    }
  },

  'sticky-note': {
    displayName: 'Sticky Note',
    description: 'Post-it style note',
    category: 'note',
    defaultAttrs: {
      position: { type: 'absolute', x: -100, y: -100, width: 200, height: 200 },
      style: { background: '#FFF740', color: '#000000' }
    }
  },

  // Media
  'canvas-image': {
    displayName: 'Image',
    description: 'Embedded image',
    category: 'media',
    defaultAttrs: {
      position: { type: 'absolute', x: -200, y: -150, width: 400, height: 300 },
      style: {}
    }
  },

  // Interactive
  'canvas-button': {
    displayName: 'Button',
    description: 'Interactive button',
    category: 'interactive',
    defaultAttrs: {
      position: { type: 'absolute', x: -60, y: -20, width: 120, height: 40 },
      style: { background: '#4A90E2', color: '#ffffff' }
    }
  },

  // Groups
  'canvas-group': {
    displayName: 'Group',
    description: 'Grouped elements',
    category: 'container',
    defaultAttrs: {
      position: { type: 'absolute', x: -200, y: -150, width: 400, height: 300 }
    }
  },

  // Connectors
  'connector': {
    displayName: 'Connector',
    description: 'Connects two elements',
    category: 'connector',
    defaultAttrs: {
      position: { type: 'absolute', x: 0, y: 0 },
      style: { stroke: '#999999', strokeWidth: 2 },
      semantic: { sourceId: '', targetId: '' }
    }
  }
};

/**
 * Canvas Application Configuration
 */
export const CANVAS_APP_CONFIG: AppConfig = {
  appId: 'canvas_v2',
  appName: 'Canvas v2',
  containerType: 'canvas',
  layoutMode: 'spatial',
  supportedClasses: CANVAS_SUPPORTED_CLASSES,
  supportedPositionTypes: ['absolute', 'fixed'],
  supportsDragDrop: true,
  supportsResize: true,
  supportsRotation: true,
  defaultContainer: {
    width: 1920,
    height: 1080,
    infinite: true // Canvas can extend infinitely
  },
  
  editorSelector: '.canvas-app-v2' // ✅ Required by AppConfig
};

/**
 * Helper: Get supported className list
 */
export function getSupportedClassNames(): string[] {
  return Object.keys(CANVAS_SUPPORTED_CLASSES);
}

/**
 * Helper: Check if className is supported
 */
export function isClassNameSupported(className: string): boolean {
  return className in CANVAS_SUPPORTED_CLASSES;
}

/**
 * Helper: Get class info
 */
export function getClassInfo(className: string): SupportedClass | undefined {
  return CANVAS_SUPPORTED_CLASSES[className];
}

/**
 * Helper: Get classes by category
 */
export function getClassesByCategory(category: string): string[] {
  return Object.entries(CANVAS_SUPPORTED_CLASSES)
    .filter(([_, info]) => info.category === category)
    .map(([className]) => className);
}

